import { CheckCircle as UiCheckCircle, XCircle as UiXCircle } from '../components/icons';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import persianDate from 'persian-date';

export default function GoldRequest() {
  const [abshodeList, setAbshodeList] = useState({ close: [], higher: [] });
  const [requests, setRequests] = useState([]);
  const [balanceGold, setBalanceGold] = useState(0);
  const [selectedAbshode, setSelectedAbshode] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      if (err.response?.status !== 401 && localStorage.getItem('refreshToken')) throw err;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const [abshodeRes, requestsRes, balanceRes] = await Promise.all([
        api.get('/api/gold/abshode', { headers: { Authorization: `Bearer ${newToken}` } }),
        api.get('/api/gold/user-requests', { headers: { Authorization: `Bearer ${newToken}` } }),
        api.get('/api/wallet/balances', { headers: { Authorization: `Bearer ${newToken}` } })
      ]);
      setAbshodeList(abshodeRes.data);
      setRequests(requestsRes.data);
      setBalanceGold(balanceRes.data.balance_gold);
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const [abshodeRes, requestsRes, balanceRes] = await Promise.all([
            api.get('/api/gold/abshode', { headers: { Authorization: `Bearer ${newToken}` } }),
            api.get('/api/gold/user-requests', { headers: { Authorization: `Bearer ${newToken}` } }),
            api.get('/api/wallet/balances', { headers: { Authorization: `Bearer ${newToken}` } })
          ]);
          setAbshodeList(abshodeRes.data);
          setRequests(requestsRes.data);
          setBalanceGold(balanceRes.data.balance_gold);
        } else {
          setError('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
          setTimeout(() => navigate('/login_page'), 2000);
        }
      } else {
        setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const handleSelect = (abshode) => {
    setSelectedAbshode(abshode);
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setTimeout(() => setSelectedAbshode(null), 300);
  };

  const handleSubmit = async () => {
    if (!selectedAbshode) {
      setError('لطفاً یک آبشده انتخاب کنید');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.post('/api/gold/request', {
        vazn: selectedAbshode.vazn,
        ayar: selectedAbshode.ayar,
        vazn750: selectedAbshode.vazn750,
        ang: selectedAbshode.ang,
        rey: selectedAbshode.rey
      }, { headers: { Authorization: `Bearer ${newToken}` } });
      setSuccess(res.data.message);
      closeSheet();
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت درخواست');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.post(`/api/gold/requests/cancel/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setSuccess(res.data.message);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در لغو درخواست');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusText = (status) => {
    const map = {
      pending: 'در انتظار تأیید',
      approved: 'تأیید شد',
      ready: 'آماده تحویل',
      delivered: 'تحویل داده شد',
      rejected: 'رد شد',
      canceled: 'لغو شده'
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'ready': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'rejected': case 'canceled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // تابع محاسبه عیار (اگر API نداد)
  const calculateAyar = (vazn, vazn750) => {
    const v = parseFloat(vazn);
    const v750 = parseFloat(vazn750);
    if (!v || !v750 || v === 0) return 'نامشخص';
    return ((v750 / v) * 750).toFixed(2);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-4xl mx-auto">

          {/* پیام‌ها */}
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
            <AnimatePresence>
              {error && (
                <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                  <div className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3">
                    <UiXCircle className="w-6 h-6" />
                    <span className="font-medium">{error}</span>
                  </div>
                </motion.div>
              )}
              {success && (
                <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                  <div className="bg-green-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-green-300 flex items-center gap-3">
                    <UiCheckCircle className="w-6 h-6" />
                    <span className="font-medium">{success}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* عنوان */}
          <motion.h2 data-page-title="true"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            درخواست آبشده طلا
          </motion.h2>

          {/* موجودی طلا */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-6 shadow-xl border border-gray-200 mb-8"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">موجودی طلایی شما</h3>
            <p className="text-3xl font-bold text-amber-700">{parseFloat(balanceGold).toFixed(3)} گرم</p>
          </motion.div>

          {/* آبشده‌های نزدیک */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6 mb-8"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-5">آبشده‌های قابل انتخاب</h3>
            <div className="block w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory">
              <table className="w-full text-xs text-right border-collapse">
                <thead className="bg-green-50 text-green-700 sticky left-0">
                  <tr>
                    <th className="px-3 py-2 font-semibold md:hidden">مشخصات آبشده</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">آزمایشگاه</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">شماره انگ</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">عیار</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">وزن (گرم)</th>
                    <th className="px-3 py-2 font-semibold">وزن 750</th>
                    <th className="px-3 py-2 font-semibold">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {abshodeList.close.length === 0 ? (
                    <tr><td colSpan="3" className="text-center py-6 text-gray-500 text-sm">هیچ آبشده‌ای مناسب وزن شما موجود نیست</td></tr>
                  ) : (
                    abshodeList.close.map((abshode, i) => (
                      <motion.tr
                        key={JSON.stringify([abshode.ang, abshode.rey, abshode.vazn, abshode.ayar, abshode.vazn750])}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="hover:bg-green-50 transition-colors snap-start"
                      >
                        <td className="px-3 py-2 md:hidden">
                          <div className="space-y-1 text-xs">
                            <div><strong>آزمایشگاه:</strong> {abshode.rey}</div>
                            <div><strong>انگ:</strong> <span className="font-mono">{abshode.ang}</span></div>
                            <div><strong>عیار:</strong> <span className="font-mono">{parseFloat(abshode.ayar).toFixed(2)}</span></div>
                            <div><strong>وزن:</strong> <span className="font-mono">{parseFloat(abshode.vazn).toFixed(3)} گرم</span></div>
                          </div>
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell">{abshode.rey}</td>
                        <td className="px-3 py-2 font-mono hidden md:table-cell">{abshode.ang}</td>
                        <td className="px-3 py-2 font-mono hidden md:table-cell">{parseFloat(abshode.ayar).toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono hidden md:table-cell">{parseFloat(abshode.vazn).toFixed(3)}</td>
                        <td className="px-3 py-2 font-mono text-green-700 font-bold">{parseFloat(abshode.vazn750).toFixed(3)}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleSelect(abshode)}
                            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:from-green-600 hover:to-emerald-700 shadow-sm hover:shadow-md transform hover:scale-105 transition-all"
                          >
                            انتخاب
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* آبشده‌های بالاتر */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6 mb-8"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-5">آبشده‌های بالاتر از موجودی</h3>
            <div className="block w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory">
              <table className="w-full text-xs text-right border-collapse">
                <thead className="bg-red-50 text-red-700 sticky left-0">
                  <tr>
                    <th className="px-3 py-2 font-semibold md:hidden">مشخصات آبشده</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">آزمایشگاه</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">شماره انگ</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">عیار</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">وزن (گرم)</th>
                    <th className="px-3 py-2 font-semibold">وزن 750</th>
                    <th className="px-3 py-2 font-semibold">کسری (گرم)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {abshodeList.higher.length === 0 ? (
                    <tr><td colSpan="3" className="text-center py-6 text-gray-500 text-sm">هیچ آبشده‌ای موجود نیست</td></tr>
                  ) : (
                    abshodeList.higher.map((abshode, i) => {
                      const shortage = parseFloat(abshode.vazn750) - balanceGold;
                      return (
                        <motion.tr
                          key={JSON.stringify([abshode.ang, abshode.rey, abshode.vazn, abshode.ayar, abshode.vazn750])}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="hover:bg-red-50 transition-colors snap-start"
                        >
                          <td className="px-3 py-2 md:hidden">
                            <div className="space-y-1 text-xs">
                              <div><strong>آزمایشگاه:</strong> {abshode.rey}</div>
                              <div><strong>انگ:</strong> <span className="font-mono">{abshode.ang}</span></div>
                              <div><strong>عیار:</strong> <span className="font-mono">{parseFloat(abshode.ayar).toFixed(2)}</span></div>
                              <div><strong>وزن:</strong> <span className="font-mono">{parseFloat(abshode.vazn).toFixed(3)} گرم</span></div>
                            </div>
                          </td>
                          <td className="px-3 py-2 hidden md:table-cell">{abshode.rey}</td>
                          <td className="px-3 py-2 font-mono hidden md:table-cell">{abshode.ang}</td>
                          <td className="px-3 py-2 font-mono hidden md:table-cell">{parseFloat(abshode.ayar).toFixed(2)}</td>
                          <td className="px-3 py-2 font-mono hidden md:table-cell">{parseFloat(abshode.vazn).toFixed(3)}</td>
                          <td className="px-3 py-2 font-mono text-red-700 font-bold">{parseFloat(abshode.vazn750).toFixed(3)}</td>
                          <td className="px-3 py-2 font-mono text-red-600 font-bold">{shortage.toFixed(3)}</td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* درخواست‌های کاربر */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-5">درخواست‌های شما</h3>
            <div className="block w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory">
              <table className="w-full text-xs text-right border-collapse">
                <thead className="bg-gray-50 text-gray-600 sticky left-0">
                  <tr>
                    <th className="px-3 py-2 font-semibold md:hidden">تاریخ</th>
                    <th className="px-3 py-2 font-semibold md:hidden">مشخصات</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">تاریخ</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">آزمایشگاه</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">شماره انگ</th>
                    <th className="px-3 py-2 font-semibold hidden md:table-cell">عیار</th>
                    <th className="px-3 py-2 font-semibold">وزن 750</th>
                    <th className="px-3 py-2 font-semibold">وضعیت</th>
                    <th className="px-3 py-2 font-semibold">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {requests.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-6 text-gray-500 text-sm">هیچ درخواستی ثبت نشده</td></tr>
                  ) : (
                    requests.map((req, i) => {
                      const persianDateStr = new persianDate(new Date(req.created_at)).format('YY/MM/DD HH:mm');
                      const ayar = req.ayar ? parseFloat(req.ayar).toFixed(2) : calculateAyar(req.vazn, req.vazn750);

                      return (
                        <motion.tr
                          key={req.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={req.status === 'pending' ? 'bg-yellow-50' : 'hover:bg-gray-50 snap-start'}
                        >
                          <td className="px-3 py-2 text-gray-600 md:hidden whitespace-nowrap">{persianDateStr}</td>
                          <td className="px-3 py-2 md:hidden">
                            <div className="space-y-1 text-xs">
                              <div><strong>آزمایشگاه:</strong> {req.rey}</div>
                              <div><strong>انگ:</strong> <span className="font-mono">{req.ang}</span></div>
                              <div><strong>عیار:</strong> <span className="font-mono">{ayar}</span></div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 hidden md:table-cell whitespace-nowrap">{persianDateStr}</td>
                          <td className="px-3 py-2 hidden md:table-cell">{req.rey}</td>
                          <td className="px-3 py-2 font-mono hidden md:table-cell">{req.ang}</td>
                          <td className="px-3 py-2 font-mono hidden md:table-cell">{ayar}</td>
                          <td className="px-3 py-2 font-mono text-indigo-700 font-bold">{parseFloat(req.vazn750).toFixed(3)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(req.status)} whitespace-nowrap`}>
                              {getStatusText(req.status)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {req.status === 'pending' && (
                              <button
                                onClick={() => handleCancelRequest(req.id)}
                                className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:from-red-600 hover:to-rose-700 shadow-sm hover:shadow-md transform hover:scale-105 transition-all"
                              >
                                لغو
                              </button>
                            )}
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>


{/* --- Bottom Sheet --- */}
{/* --- Bottom Sheet --- */}
<AnimatePresence>
  {isSheetOpen && selectedAbshode && (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeSheet}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
      />

      {/* Sheet */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="
          fixed 
          left-0 
          right-0 
          bg-white 
          rounded-t-3xl 
          shadow-2xl 
          z-50 
          overflow-y-auto
          max-h-[calc(100vh-var(--bottom-nav-height))]
          pb-8
        "
        style={{
          bottom: 'var(--bottom-nav-height, 80px)', // شروع از بالای نوار
          '--bottom-nav-height': '60px', // ارتفاع نوار پایین (قابل تغییر)
        }}
      >
        <div className="p-6 pb-safe">

          {/* Drag Handle */}
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />

          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            تأیید درخواست آبشده
          </h3>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-200">
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">آزمایشگاه:</span>
                <span className="font-medium">{selectedAbshode.rey}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">شماره انگ:</span>
                <span className="font-mono font-bold">{selectedAbshode.ang}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">عیار:</span>
                <span className="font-mono">{parseFloat(selectedAbshode.ayar).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">وزن:</span>
                <span className="font-mono font-bold">{parseFloat(selectedAbshode.vazn).toFixed(3)} گرم</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">وزن 750:</span>
                <span className="font-mono text-green-700 font-bold">{parseFloat(selectedAbshode.vazn750).toFixed(3)} گرم</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
            >
              ارسال درخواست
            </button>
            <button
              onClick={closeSheet}
              className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-red-600 hover:to-rose-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
            >
              لغو
            </button>
          </div>
        </div>
      </motion.div>
    </>
  )}
</AnimatePresence>

        </div>
      </div>
    </>
  );
}